import { useEffect, useState } from "react";
import { publishApi } from "../api/client";
import type { HereNowConnection } from "../types";
import { useToast } from "../lib/toast";
import "./HereNowConnectCard.css";

type Step = "email" | "code" | "key";

/**
 * Per-user here.now connection. The key is private to the signed-in user —
 * unlike the admin-managed plugins listed below it — and never comes back
 * from the server after it is stored; only its last four characters do.
 */
export default function HereNowConnectCard() {
  const toast = useToast();
  const [conn, setConn] = useState<HereNowConnection | null>(null);
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    publishApi.connection().then(setConn).catch(() => setConn({ connected: false, email: null, key_hint: null, connected_at: null }));
  }, []);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const connected = (next: HereNowConnection) => {
    setConn(next);
    setStep("email");
    setCode("");
    setApiKey("");
    toast.success("here.now connected");
  };

  return (
    <section className="card herenow-card" aria-labelledby="herenow-title">
      <div className="herenow-card-head">
        <div>
          <h3 id="herenow-title" className="herenow-card-title">🌐 here.now publishing</h3>
          <p className="herenow-card-sub">
            Publish canvases as websites. Without an account, sites are anonymous and last 24 hours;
            connect your own here.now account for permanent sites in your Home or workspaces.
            This connection is private to you.
          </p>
        </div>
        {conn?.connected && <span className="tag tag-success">Connected</span>}
      </div>

      {conn === null ? null : conn.connected ? (
        <div className="herenow-card-row">
          <span className="herenow-card-muted">
            {conn.email ?? "API key"}{conn.key_hint ? ` · key ${conn.key_hint}` : ""}
          </span>
          <span className="spacer" />
          <a className="btn btn-sm" href="https://here.now/dashboard" target="_blank" rel="noopener noreferrer">Open dashboard</a>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            disabled={busy}
            onClick={() => {
              if (!window.confirm("Disconnect here.now? Published sites stay online; chatter just forgets your key.")) return;
              void run(async () => {
                await publishApi.disconnect();
                setConn({ connected: false, email: null, key_hint: null, connected_at: null });
              });
            }}
          >
            Disconnect
          </button>
        </div>
      ) : step === "key" ? (
        <form
          className="herenow-card-row"
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => connected(await publishApi.connectKey(apiKey)));
          }}
        >
          <input
            className="input"
            type="password"
            autoComplete="off"
            placeholder="hnk_…"
            aria-label="here.now API key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
          <button className="btn btn-primary" disabled={busy || !apiKey.trim()}>Save key</button>
          <button type="button" className="btn btn-ghost" onClick={() => setStep("email")}>Use email instead</button>
        </form>
      ) : step === "code" ? (
        <form
          className="herenow-card-row"
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => connected(await publishApi.verifyCode(email, code)));
          }}
        >
          <span className="herenow-card-muted">Enter the code here.now emailed to {email}</span>
          <input
            className="input herenow-card-code"
            autoComplete="one-time-code"
            placeholder="ABCD-2345"
            aria-label="Sign-in code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <button className="btn btn-primary" disabled={busy || !code.trim()}>Connect</button>
          <button type="button" className="btn btn-ghost" onClick={() => setStep("email")}>Back</button>
        </form>
      ) : (
        <form
          className="herenow-card-row"
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              await publishApi.requestCode(email);
              setStep("code");
            });
          }}
        >
          <input
            className="input"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            aria-label="here.now account email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button className="btn btn-primary" disabled={busy || !email.trim()}>Email me a code</button>
          <button type="button" className="btn btn-ghost" onClick={() => setStep("key")}>Paste an API key</button>
        </form>
      )}
      {!conn?.connected && step !== "key" && (
        <p className="herenow-card-fine">
          Signing in with a new email creates a free here.now account for it.
        </p>
      )}
    </section>
  );
}
